import React, { useState, useEffect, Suspense, lazy } from "react";
import { Menu, UserCog, LogOut } from "lucide-react";
import Sidebar from "./components/Sidebar";
import Login from "./components/Login";
import Landing from "./components/Landing";
import TickerSearch from "./components/TickerSearch";
import ProfileMenu from "./components/ProfileMenu";
import MarketClock from "./components/MarketClock";
import MarketTicker from "./components/MarketTicker";
import { isAuthenticated, logout, getMyProfile, exitImpersonation } from "./api";
import { ThemeProvider } from "./theme";

// Route-level code splitting: each page (and its dependencies, e.g. recharts
// for ChartView) ships as its own chunk instead of bloating the initial
// bundle every visitor downloads before seeing the Dashboard.
const Dashboard = lazy(() => import("./components/Dashboard"));
const Screener = lazy(() => import("./components/Screener"));
const ChartView = lazy(() => import("./components/ChartView"));
const Portfolio = lazy(() => import("./components/Portfolio"));
const Chips = lazy(() => import("./components/Chips"));
const ETFs = lazy(() => import("./components/ETFs"));
const Movers = lazy(() => import("./components/Movers"));
const GlobalScan = lazy(() => import("./components/GlobalScan"));
const TopStocks = lazy(() => import("./components/TopStocks"));
const SectorPage = lazy(() => import("./components/SectorPage"));
const MarketNews = lazy(() => import("./components/MarketNews"));
const Profile = lazy(() => import("./components/Profile"));
const Monitor = lazy(() => import("./components/Monitor"));
const Reports = lazy(() => import("./components/Reports"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-full py-24">
      <div className="flex flex-col items-center gap-3">
        <div className="relative w-9 h-9">
          <div className="absolute inset-0 rounded-full border-2" style={{ borderColor: "var(--border-color)" }} />
          <div
            className="absolute inset-0 rounded-full border-2 border-transparent animate-spin"
            style={{ borderTopColor: "var(--accent-blue)", borderRightColor: "var(--accent-blue)" }}
          />
        </div>
        <span className="text-xs" style={{ color: "var(--text-muted)" }}>Loading…</span>
      </div>
    </div>
  );
}

export default function App() {
  const [authed, setAuthed] = useState(isAuthenticated());
  const [showLogin, setShowLogin] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [activePage, setActivePage] = useState("dashboard");
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [myProfile, setMyProfile] = useState(null);

  useEffect(() => {
    const handleUnauthorized = () => {
      setAuthed(false);
      setShowLogin(false);
    };
    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () => window.removeEventListener("auth:unauthorized", handleUnauthorized);
  }, []);

  useEffect(() => {
    if (!authed) { setMyProfile(null); return; }
    getMyProfile().then(setMyProfile).catch(() => {});
  }, [authed]);

  if (!authed) {
    if (!showLogin) {
      return (
        <ThemeProvider authed={false}>
          <Landing
            onLogin={() => { setAuthMode("login"); setShowLogin(true); }}
            onGetStarted={() => { setAuthMode("register"); setShowLogin(true); }}
          />
        </ThemeProvider>
      );
    }
    return (
      <ThemeProvider authed={false}>
        <Login initialMode={authMode} onSuccess={() => setAuthed(true)} onBack={() => setShowLogin(false)} />
      </ThemeProvider>
    );
  }

  const navigateToChart = (ticker) => {
    setSelectedTicker(ticker);
    setActivePage("chart");
  };

  const renderPage = () => {
    switch (activePage) {
      case "dashboard":
        return <Dashboard onTickerClick={navigateToChart} />;
      case "marketnews":
        return <MarketNews />;
      case "profile":
        return <Profile />;
      case "screener":
        return <Screener onTickerClick={navigateToChart} />;
      case "chart":
        return (
          <ChartView
            initialTicker={selectedTicker}
            onBack={() => setActivePage("screener")}
          />
        );
      case "topstocks":
        return <TopStocks onTickerClick={navigateToChart} />;
      case "chips":
        return <Chips onTickerClick={navigateToChart} />;
      case "etfs":
        return <ETFs onTickerClick={navigateToChart} />;
      case "sectors":
        return <SectorPage onTickerClick={navigateToChart} />;
      case "movers":
        return <Movers onTickerClick={navigateToChart} />;
      case "globalscan":
        return <GlobalScan onTickerClick={navigateToChart} />;
      case "portfolio":
        return <Portfolio onTickerClick={navigateToChart} />;
      case "monitor":
        return <Monitor onNavigateProfile={() => setActivePage("profile")} />;
      case "reports":
        return <Reports />;
      case "admin":
        return <AdminDashboard />;
      default:
        return <Dashboard onTickerClick={navigateToChart} />;
    }
  };

  return (
    <ThemeProvider serverTheme={myProfile?.theme} authed={authed}>
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: "var(--bg-page)" }}>
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 md:hidden"
          style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <Sidebar
        activePage={activePage}
        setActivePage={(page) => {
          setActivePage(page);
          setSidebarOpen(false);
        }}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isAdmin={!!myProfile?.is_admin}
      />
      <main className="flex-1 flex flex-col overflow-hidden">
        {myProfile?.impersonated_by && (
          <div
            className="flex items-center justify-center gap-2 px-4 py-2 text-sm shrink-0"
            style={{ backgroundColor: "#2d2515", color: "#eab308", borderBottom: "1px solid #eab308" }}
          >
            <UserCog size={14} />
            Viewing as <strong>{myProfile.username}</strong> — logged in by admin {myProfile.impersonated_by}
            <button
              onClick={() => { exitImpersonation(); window.location.reload(); }}
              className="ml-2 flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors"
              style={{ backgroundColor: "#eab308", color: "#1a1a0a" }}
            >
              <LogOut size={11} />
              Exit
            </button>
          </div>
        )}
        <div
          className="flex items-center gap-3 px-5 py-3 shrink-0 relative z-20"
          style={{ backgroundColor: "var(--bg-shell)", borderBottom: "1px solid var(--border-color)", boxShadow: "0 1px 0 rgba(0,0,0,0.4), 0 4px 16px -8px rgba(0,0,0,0.5)" }}
        >
          <button
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
            className="md:hidden shrink-0"
            style={{ color: "var(--text-primary)" }}
          >
            <Menu size={22} />
          </button>
          <div className="min-w-0 flex-1">
            <TickerSearch onSelect={navigateToChart} />
          </div>
          <MarketTicker />
          <div className="ml-auto flex items-center gap-3">
            <div className="hidden sm:block">
              <MarketClock />
            </div>
            <div className="w-px h-6 hidden sm:block" style={{ backgroundColor: "var(--border-color)" }} />
            <ProfileMenu onNavigateProfile={() => setActivePage("profile")} onLogout={logout} />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <Suspense fallback={<PageLoader />}>
            {renderPage()}
          </Suspense>
        </div>
      </main>
    </div>
    </ThemeProvider>
  );
}
